import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Attachment, attachmentPreviewUrl } from '@/entities/attachment';
import { Dialog } from '@/shared/ui';

import styles from './Attachments.module.css';

interface Props {
  /** The previewable images of the card, in list order. */
  images: Attachment[];
  startId: string;
  onClose: () => void;
}

/** Full-size view of the card's images: arrows or keys step through them. */
export function AttachmentLightbox({ images, startId, onClose }: Props) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(() =>
    Math.max(
      0,
      images.findIndex((image) => image.id === startId),
    ),
  );
  const current = images[index];
  const step = (delta: number) => {
    setIndex((value) => (value + delta + images.length) % images.length);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (images.length < 2) return;
      if (event.key === 'ArrowLeft') step(-1);
      else if (event.key === 'ArrowRight') step(1);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `step` only uses the length
  }, [images.length]);

  if (!current) return null;
  return (
    <Dialog title={current.filename} onClose={onClose} size="lg">
      <div className={styles.lightbox} data-testid="lightbox">
        <img
          className={styles.lightboxImage}
          src={attachmentPreviewUrl(current.id, 'large')}
          alt={current.filename}
        />
        <div className={styles.lightboxBar}>
          {images.length > 1 && (
            <button
              type="button"
              className={styles.step}
              aria-label={t('attachments.previous')}
              onClick={() => {
                step(-1);
              }}
            >
              ‹
            </button>
          )}
          <span className={styles.sub}>
            {index + 1} / {images.length}
          </span>
          {images.length > 1 && (
            <button
              type="button"
              className={styles.step}
              aria-label={t('attachments.next')}
              onClick={() => {
                step(1);
              }}
            >
              ›
            </button>
          )}
          <a
            className={styles.link}
            href={attachmentPreviewUrl(current.id, 'original')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('attachments.openOriginal')}
          </a>
        </div>
      </div>
    </Dialog>
  );
}
